import {Component, EventEmitter, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {
    ProductDto,
    QuotationControllerService,
    QuotationDto,
    QuotationLineDto,
    QuotationLookupDto, RfqDto,
    SelectOption, SupplierDto
} from '../../../../@api';
import {FormGroup, NgForm} from '@angular/forms';
import {Message} from 'primeng/api';
import {ProgressIndicatorService} from '../../../../@shared';
import {MessagingHandlerService} from '../../../../common/response-messaging-handler/messaging-handler.service';

const ERROR_VALIDATION = 'Please correct errors on the indicated fields.';
const DELAY: number = 1000;

@Component({
    selector: 'secure-quotation',
    templateUrl: './quotation.component.html',
    styleUrls: ['./quotation.component.scss']
})
export class QuotationComponent implements OnInit {

    @ViewChild('quotationForm') quotationForm: NgForm;

    toggleProductListDialog: EventEmitter<boolean> = new EventEmitter<boolean>();
    quotationLine!: QuotationLineDto;

    uuid: string;
    number!: string;
    quotation!: QuotationDto;
    lookups: QuotationLookupDto = { products: [] };
    cols: any[] = [];
    vatMultiplier: number = 100;
    vatDivider: number = 100;
    errors: Message[] = [];
    categoryOptions: any[] = [
        {label: 'Batteries', value: 'Batteries'},
        {label: 'Inverters', value: 'Inverters'},
        {label: 'PV Panels', value: 'PV Panels'}
    ];
    serviceOptions: any[] = [
        {label: 'Yes', value: true},
        {label: 'No', value: false}
    ];
    statusOptions: any[] = [
        {label: 'Created', value: 'CREATED'},
        {label: 'Pending', value: 'PENDING'},
        {label: 'Verified', value: 'VERIFIED'},
        {label: 'Complete', value: 'COMPLETE'}
    ];
    supplierOptions: any[] = [];
    etaDate?: Date;
    initialised!: boolean;

    constructor(private route: ActivatedRoute,
                private quotationService: QuotationControllerService,
                private progress: ProgressIndicatorService,
                private messageService: MessagingHandlerService) {
        this.showProgress();
    }

    ngOnInit(): void {
        this.route.url.subscribe((): void => {
            this.number = this.route.snapshot.paramMap.get('number') || '';

            this.quotationService.findQuotationLookupData().subscribe((lookup: QuotationLookupDto): void => {
                this.initComponentDataWithLookups(lookup);

                this.quotationService.findQuotationByNumber(this.number).subscribe((quotation: QuotationDto): void => {
                    this.initQuotation(quotation);
                    this.hideProgress(DELAY);
                }, error => {
                    console.log(error);
                    this.hideProgress(DELAY);
                    this.messageService.showError('Could not load quotation by number: ' + this.number, 'Request Failed');
                });
            }, error => {
                console.log(error);
                this.hideProgress(DELAY);
            });
        });
    }

    initQuotation(quotation: QuotationDto): void {
        this.etaDate = new Date(quotation.eta);
        quotation.eta = this.etaDate;

        this.quotation = quotation;
        this.quotation.eta = (quotation.eta) ? new Date(quotation.eta) : undefined;
        this.vatDivider = this.vatDivider + (this.quotation.supplier?.vatPercentage || 0);

        (this.quotation.lines || []).sort(this.quotationLineComparator);
        (this.quotation.lines || []).forEach((line: QuotationLineDto): void => {
            line['type'] = (line.product && this.isPositiveNumber(line.product.id)) ? 'product' : 'text';
        });
    }
    
    showProgress(): void {
        this.progress.publish(true);
    }

    hideProgress(timeout: number = 100): void {
        setTimeout((): void => {
            this.progress.publish(false);
            if (!this.initialised) {
                this.initialised = true;
            }
        }, timeout);
    }

    initComponentDataWithLookups(lookups: QuotationLookupDto): void {
        this.lookups = lookups;
        this.supplierOptions = lookups.suppliers
            .map((s: SupplierDto): any => ({value: s, label: s.displayName}));
    }

    lineProductSelectionChanged(line: QuotationLineDto, newProductId: any): void {
        try {
            let option: SelectOption = this.lookups.products.filter((f: SelectOption): boolean => f.value === newProductId)[0];
            let oldUnitPrice: number = line['unitPrice'] || 0;
            let newUnitPrice: number = Number((option.price || 0).toFixed(2));
            line['unitPrice'] = newUnitPrice;

            this.lineUnitPriceChanged(line, newUnitPrice, {model: oldUnitPrice});
        } catch (e) {
            let oldUnitPrice: number = line['unitPrice'] || 0;
            line['unitPrice'] = undefined;
            this.lineUnitPriceChanged(line, 0.00, {model: oldUnitPrice});
        }
    }

    lineQtyChanged(line: QuotationLineDto, newQty: any, oldQtyModel: any): void {
        let oldQty: number = oldQtyModel.model ? Number(oldQtyModel.model) : 0;

        line['qty'] = Number(newQty);
        let lineUnitPrice: number = line['unitPrice'] || 0;
        let lineOldTotal: number = lineUnitPrice * oldQty || 0;
        let lineNewTotal: number = lineUnitPrice * Number(newQty);
        line['total'] = Number(lineNewTotal.toFixed(2));

        let lineSubTotal: number = Number(((lineNewTotal * this.vatMultiplier) / this.vatDivider).toFixed(2));
        line['subTotal'] = lineSubTotal;
        line['vatTotal'] = Number((lineNewTotal - lineSubTotal).toFixed(2));

        this.calculateQuotationTotals(lineOldTotal, lineNewTotal);
    }

    lineUnitPriceChanged(line: QuotationLineDto, newUnitPrice: any, oldUnitPriceModel: any): void {
        let oldUnitPrice: number = oldUnitPriceModel.model ? Number(oldUnitPriceModel.model) : 0;

        line['unitPrice'] = Number(newUnitPrice);
        let lineQty: number = line['qty'] || 0;
        let lineNewTotal: number = lineQty * Number(newUnitPrice);
        lineNewTotal = Number(lineNewTotal.toFixed(2));
        line['total'] = lineNewTotal;

        let lineSubTotal: number = Number(((lineNewTotal * this.vatMultiplier) / this.vatDivider).toFixed(2));
        line['subTotal'] = lineSubTotal;
        line['vatTotal'] = Number((lineNewTotal - lineSubTotal).toFixed(2));

        let lineOldTotal = lineQty * oldUnitPrice || 0;
        this.calculateQuotationTotals(lineOldTotal, lineNewTotal);
    }

    calculateQuotationTotals(oldLineTotal: number, newLineTotal: number): void {
        this.quotation.total = this.quotation.total - oldLineTotal + newLineTotal;

        let oldLineSubTotal: number = Number(((oldLineTotal * this.vatMultiplier) / this.vatDivider).toFixed(2));
        let newLineSubTotal: number = Number(((newLineTotal * this.vatMultiplier) / this.vatDivider).toFixed(2));
        this.quotation.subTotal = this.quotation.subTotal - oldLineSubTotal + newLineSubTotal;

        this.quotation.vatTotal = Number((this.quotation.total - this.quotation.subTotal).toFixed(2));
    }

    ngModelChanged($event): void {
        this.validateForm(this.quotationForm.form, false);
    }

    validateForm(form: FormGroup, validateForErrors: boolean): boolean {
        let formValid = true;
        let errorElement = this.quotationForm.form.controls['formHidden'];
        try {
            this.quotationForm.form.markAllAsTouched();
            
            let quotationLines: QuotationLineDto[] = this.quotation.lines;
            quotationLines.forEach((l: QuotationLineDto): void => {
                
            });
        } catch (e) {
            console.log(e);
        } finally {
            if (!formValid && validateForErrors) {
                errorElement.setErrors({formInvalid: true});
                this.errors.push({severity: 'error', detail: ERROR_VALIDATION});
            }
            if (!validateForErrors && formValid) {
                errorElement.setErrors(null);
                this.errors = [];
            }
            form.markAllAsTouched();
        }

        return formValid;
    }

    openProductListDialog(quotationLine: QuotationLineDto): void {
        this.quotationLine = quotationLine;
        this.toggleProductListDialog.emit(true);
    }

    onProductSelectCallback(product: ProductDto): void {
        this.quotationLine.product = product;
        this.quotationLine['description'] = product.description;
        this.quotationLine['type'] = 'product';
        this.quotationLine['notes'] = undefined;
        this.lineProductSelectionChanged(this.quotationLine, product.id);
    }

    quotationLineComparator(a: QuotationLineDto, b: QuotationLineDto): number {
        // Compare objects with products first
        const hasProductA = !!a.product;
        const hasProductB = !!b.product;

        if (hasProductA && !hasProductB) return -1;
        if (!hasProductA && hasProductB) return 1;

        // Both have products, sort by category
        if (hasProductA && hasProductB) {
            const categoryA = a.product!.category ?? '';
            const categoryB = b.product!.category ?? '';
            return categoryA.localeCompare(categoryB);
        }

        // Neither has product, sort by description
        const descriptionA = a.description ?? '';
        const descriptionB = b.description ?? '';
        return descriptionA.localeCompare(descriptionB);
    }

    submit(): void {
        this.errors = [];
        this.validateForm(this.quotationForm.form, true);
        if (!this.quotationForm.form.invalid) {
            let request: QuotationDto = this.prepareRequestDates();

            this.showProgress();
            this.quotationService.submitQuotation(request).subscribe((response: QuotationDto): void => {
                setTimeout((): void => {
                    this.messageService.showSuccess('Quotation updated successfully', this.quotation.number);
                    this.hideProgress();
                }, DELAY);
            }, error => {
                console.log(JSON.stringify(error));
                this.hideProgress(DELAY);
                this.messageService.showError('Update failed', this.quotation.number);
            });
        }
    }

    prepareRequestDates(): QuotationDto {
        let etaDate = JSON.parse(JSON.stringify(this.etaDate));
        this.quotation.eta = (this.quotation.eta === this.etaDate)
            ? this.adjustDate(this.quotation.eta, -1) : this.adjustDate(etaDate, -1);

        return this.quotation;
    }

    onRemoveLineClick(line: QuotationLineDto): void {
        this.quotation.lines.splice(this.quotation.lines.indexOf(line), 1);
        let lineTotal: number = line.total || 0.00;
        this.calculateQuotationTotals(lineTotal, 0.00);
    }

    onAddLineClick(): void {
        let line: any = { id: 0, qty: 0, unitPrice: 0.00, subTotal: 0.00, vatTotal: 0.00, total: 0.00, type: 'text' };
        this.quotation.lines.push(...[line]);
    }

    isNumber(value?: any): boolean {
        if (value === null || value === undefined || value.toString() === '' || isNaN(value)) {
            return false;
        }
        return true;
    }

    isPositiveNumber(value?: any): boolean {
        if (this.isNumber(value)) {
            return +value > 0;
        }
        return false;
    }

    removeProductFromLine(line: QuotationLineDto): void {
        line['type'] = 'text';
        line['product'] = undefined;
        line['description'] = undefined;
        line['notes'] = undefined;
    }

    /** TAKE NOTE: Multiplier can be +1 or -1 depending on whether you want to
     *  adjust the date GMT+2 or GMT-2 (timezoneOffset *= multiplier) */
    adjustDate(date: Date, multiplier: number): Date {
        if (date) {
            date = new Date(date);
            let now: Date = new Date();
            let timezoneOffset: number = now.getTimezoneOffset();
            timezoneOffset = timezoneOffset *= multiplier;
            return new Date(date.getTime() + (timezoneOffset * 60000));
        }
        return date;
    }
}
